/* eslint-disable react/prop-types, no-undef */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';
import { Button, TextField } from '@material-ui/core';

import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import toast from 'react-hot-toast';
import ReactSelect from 'react-select';
import { makeStyles } from '@material-ui/core/styles';
import {
  DataTypeProvider,
  EditingState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  PagingState,
  SearchState,
  SortingState
} from '@devexpress/dx-react-grid';
import {
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableEditColumn,
  TableEditRow,
  TableHeaderRow,
  TableInlineCellEditing,
  Toolbar
} from '@devexpress/dx-react-grid-material-ui';
import employeeQualificationService from '../../services/EmployeeQualificationService';
import employeeService from '../../services/EmployeeService';
import { base64toBlob } from './helper';
import prepareQualificationUpload from '../Qualification/helper/prepareQualificationUpload';
import ImageFullScreenDialog from '../../shared/dialog/ImageFullScreenDialog';
import KebabCommandCell from '../Qualification/table/customTemplate/KebabCommandCell';
import { CalendarProvider, QualificationFocusableCell } from '../Qualification/table/QualificationFocusableCell';
import { getQualificationStatus, QUALIFICATION_INLINE_COLUMNS } from '../Qualification/helper/helper';
import useQualificationUpload from '../Qualification/helper/useQualificationUpload';
import { MyQualificationPopup, PopupEditing } from './MyQualificationPopupEditing';

const NO_DATE_QUALIFICATIONS = ['Superannuation', 'Long Service Leave', 'Redundancy'];
const INLINE_COLUMNS = [
  ...QUALIFICATION_INLINE_COLUMNS.filter((column) => column !== 'employeeMandatoryQualifications')
];
const EDITABLE_COLUMNS = [...INLINE_COLUMNS, 'qualificationUrl'];
const WIDTHS = [
  { columnName: 'name', width: 200 },
  { columnName: 'expiryDate', width: 180 },
  { columnName: 'issueDate', width: 180 },
  { columnName: 'referenceNumber', width: 180 },
  { columnName: 'qualificationUrl', width: 180 },
  { columnName: 'status', width: 180 }
];

const ImageProvider = (props) => {
  return <DataTypeProvider formatterComponent={ImageFullScreenDialog} {...props} />;
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    '& > div:first-of-type': { display: 'none' },
    '& #add-qualification-btn': { marginRight: theme.spacing(1) }
  }
}));

const dateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};
const idValue = (record) => String(record?._id || record?.id || record?.qualificationRecordId || '');
const qualIdValue = (record) => String(record?.qualificationId || record?.id || '');
const isFile = (value) => typeof File !== 'undefined' && value instanceof File;
const recordTimestamp = (record) => {
  const createdAt = Date.parse(record?.createdAt || '');
  if (Number.isFinite(createdAt)) return createdAt;
  const updatedAt = Date.parse(record?.updatedAt || '');
  return Number.isFinite(updatedAt) ? updatedAt : 0;
};
const latestRecord = (records) =>
  [...records].sort(
    (left, right) =>
      recordTimestamp(right) - recordTimestamp(left) ||
      String(right?._id || right?.id || '').localeCompare(String(left?._id || left?.id || ''))
  )[0];
const isPendingRow = (row) => row?._pending || String(row?.status || '').toLowerCase() === 'pending';
const hasSubmittedQualification = (record, fromQbo = false) => {
  const status = String(record?.status || '').toLowerCase();
  if (status === 'not submitted') return false;
  if (['pending', 'accepted', 'expired', 'rejected'].includes(status)) return true;
  return Boolean(
    fromQbo ||
      record?.referenceNumber ||
      record?.issueDate ||
      record?.expiryDate ||
      record?.documentId ||
      record?.qualificationUrl ||
      record?.fileContent ||
      record?.content?.bytes
  );
};

function QualificationCommandsCell({ children, isAdmin, ...props }) {
  if (!isAdmin && isPendingRow(props.row)) return <TableEditColumn.Cell {...props} />;
  return <KebabCommandCell {...props}>{children}</KebabCommandCell>;
}

function ClickableMyQualificationRow({ row, onOpen, isAdmin, ...props }) {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      (!isAdmin && isPendingRow(row)) ||
      event.target.closest('button, a, input, [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(row.id);
  };

  return <Table.Row {...props} onDoubleClick={handleDoubleClick} />;
}

function ToolbarRoot({ children, onAdd, adding, submitting, ...props }) {
  const classes = useToolbarStyles();
  return (
    <Toolbar.Root {...props} className={classes.root}>
      <Button
        id="add-qualification-btn"
        variant="contained"
        color="primary"
        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
        onClick={onAdd}
        disabled={adding || submitting}
      >
        Add Qualification
      </Button>
      {children}
    </Toolbar.Root>
  );
}

function StatusFormatter({ value, row }) {
  const status = row?._pending
    ? 'Pending'
    : getQualificationStatus(value, row?.expiryDate, row?.issueDate, row?.referenceNumber);
  const color =
    status.toLowerCase() === 'accepted' ? '#16803c' : status.toLowerCase() === 'pending' ? '#ca8a04' : '#dc2626';
  return <span style={{ color, fontWeight: 600 }}>{status}</span>;
}

function QualificationNameFormatter({ value }) {
  return <>{value?.label || value || ''}</>;
}

function QualificationTableEditCell({
  column,
  value,
  row,
  onValueChange,
  onQualificationChange,
  qualificationOptions,
  onSelectFile,
  fileBusy,
  fileError,
  ...props
}) {
  if (column.name === 'name') {
    return (
      <TableEditRow.Cell {...props} editingEnabled>
        <ReactSelect
          aria-label="Qualification name"
          options={qualificationOptions}
          value={value || null}
          onChange={onQualificationChange || onValueChange}
          isSearchable
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </TableEditRow.Cell>
    );
  }
  if (column.name === 'qualificationUrl') {
    return (
      <TableEditRow.Cell {...props} editingEnabled>
        <div>
          <Button component="label" size="small" disabled={fileBusy}>
            {fileBusy ? 'Converting…' : value ? 'Replace file' : 'Upload file'}
            <input
              hidden
              type="file"
              accept="image/*,.heic,.heif,.pdf"
              onChange={(event) => onSelectFile(event, row)}
            />
          </Button>
          {value && <span style={{ fontSize: 12 }}>{isFile(value) ? value.name : 'File attached'}</span>}
          {fileError && (
            <div role="alert" style={{ color: '#c62828', fontSize: 12 }}>
              {fileError}
            </div>
          )}
        </div>
      </TableEditRow.Cell>
    );
  }
  if (column.name === 'status') return <TableEditRow.Cell {...props} value="Pending review" editingEnabled={false} />;
  if (!['referenceNumber', 'expiryDate', 'issueDate'].includes(column.name)) {
    return <TableEditRow.Cell {...props} editingEnabled={false} />;
  }
  if (column.name === 'expiryDate' || column.name === 'issueDate') {
    return (
      <TableEditRow.Cell {...props} editingEnabled>
        <TextField
          type="date"
          size="small"
          value={value || ''}
          onChange={(event) => onValueChange(event.target.value || null)}
          inputProps={{ 'aria-label': column.name === 'issueDate' ? 'Issue date' : 'Expiry date' }}
        />
      </TableEditRow.Cell>
    );
  }
  return (
    <TableEditRow.Cell {...props} editingEnabled>
      <TextField size="small" value={value || ''} onChange={(event) => onValueChange(event.target.value)} />
    </TableEditRow.Cell>
  );
}

const MyQualificationTable = () => {
  const [rows, setRows] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [addedRows, setAddedRows] = useState([]);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState([{ columnName: 'name', direction: 'asc' }]);
  const isAdmin = localStorage.getItem('role') === 'Admin';
  const [employeeKeyPayId, setEmployeeKeyPayId] = useState('');
  const { selectFile, cancelConversion, isConverting, fileError } = useQualificationUpload();
  const latestRows = useRef(rows);
  const hasLoaded = useRef(false);
  const editingModeRef = useRef(null);
  latestRows.current = rows;

  const loadRows = useCallback(async () => {
    if (!hasLoaded.current) setLoading(true);
    try {
      const [qboResponse, employeeResponse, employeeDetailsResponse] = await Promise.all([
        employeeQualificationService.getQboQualification(),
        employeeQualificationService.getEmployeeQualifications(),
        employeeService.getEmployeeDetails().catch(() => null)
      ]);
      const qbo = qboResponse?.data?.data || [];
      const employeeRecords = employeeResponse?.data?.data || [];
      const currentEmployeeKeyPayId =
        employeeDetailsResponse?.data?.data?.KeyPayEmployeeId ||
        employeeDetailsResponse?.data?.data?.employeeKeyPayId ||
        employeeRecords[0]?.employeeKeyPayId ||
        '';
      setEmployeeKeyPayId(currentEmployeeKeyPayId);
      // The employee QBO response omits Verd DB submissions for qualifications
      // that do not yet exist in QBO. The name lookup is scoped to the logged-in
      // employee and returns their pending Verd DB submissions.
      const pendingResponses = await Promise.allSettled(
        qbo.map((definition) => employeeQualificationService.getQualificationByName(definition.name))
      );
      const pendingRecords = pendingResponses.flatMap((result) =>
        result.status === 'fulfilled' ? result.value?.data?.data || [] : []
      );
      const byQualId = new Map(employeeRecords.map((record) => [String(record.qualificationId), record]));
      const mergedRecords = await Promise.all(
        qbo.map(async (definition) => {
          const key = qualIdValue(definition);
          const fromQbo = byQualId.get(key) || {};
          const dbRecord = latestRecord(
            pendingRecords.filter((record) => qualIdValue(record) === key || record.name === definition.name)
          );
          // The employee endpoint merges its newest Verd record into each KeyPay
          // qualification. Prefer that canonical record; use the pending lookup
          // only when the qualification does not exist in KeyPay yet.
          const sourceRecord = fromQbo.qualificationId ? fromQbo : dbRecord;
          // The employee grid contains submitted qualifications only. QBO definitions
          // with no employee record remain available through the Add Qualification picker.
          if (!sourceRecord || !hasSubmittedQualification(sourceRecord, Boolean(fromQbo.qualificationId))) return null;
          const content = sourceRecord.content;
          let contentFile = null;
          if (content?.bytes) {
            const filename = content.filename || 'qualification';
            const isPdf = content.contentType === 'application/pdf' || /\.pdf$/i.test(filename);
            const contentType = isPdf ? 'application/pdf' : content.contentType || 'application/octet-stream';
            const blob = base64toBlob(content.bytes, contentType, filename);
            try {
              contentFile = await prepareQualificationUpload(new File([blob], filename, { type: contentType }));
            } catch (error) {
              contentFile = new File([blob], filename, { type: contentType });
            }
          }
          const file =
            sourceRecord.qualificationUrl || contentFile || fromQbo.fileContent || definition.qualificationUrl || '';
          return {
            id: idValue(sourceRecord) || `qbo-${key}`,
            qualificationId: definition.qualificationId || definition.id,
            name: definition.name || sourceRecord.name || '',
            referenceNumber: sourceRecord.referenceNumber ?? fromQbo.cardNumber ?? definition.referenceNumber ?? '',
            issueDate: dateValue(sourceRecord.issueDate ?? fromQbo.issueDate ?? definition.issueDate),
            expiryDate: dateValue(sourceRecord.expiryDate ?? fromQbo.expiryDate ?? definition.expiryDate),
            qualificationUrl: file,
            _attachmentFromQbo: Boolean(contentFile),
            documentId:
              sourceRecord.documentId ||
              sourceRecord.documents?.[0]?.id ||
              fromQbo.documentId ||
              definition.documents?.[0]?.id ||
              null,
            employeeKeyPayId: sourceRecord.employeeKeyPayId || currentEmployeeKeyPayId,
            status: getQualificationStatus(
              sourceRecord.status || fromQbo.status || (fromQbo.qualificationId ? 'accepted' : ''),
              sourceRecord.expiryDate ?? fromQbo.expiryDate,
              sourceRecord.issueDate ?? fromQbo.issueDate,
              sourceRecord.referenceNumber ?? fromQbo.cardNumber
            ),
            _pending: String(sourceRecord.status || '').toLowerCase() === 'pending'
          };
        })
      );
      const merged = mergedRecords.filter(Boolean);

      // Keep employee and pending records even when a definition was removed from QBO.
      [...employeeRecords, ...pendingRecords]
        .sort((left, right) => recordTimestamp(right) - recordTimestamp(left))
        .forEach((record) => {
          if (!hasSubmittedQualification(record)) return;
          if (
            merged.some(
              (item) =>
                String(item.qualificationId) === qualIdValue(record) ||
                String(item.name || '')
                  .trim()
                  .toLowerCase() ===
                  String(record.name || '')
                    .trim()
                    .toLowerCase()
            )
          )
            return;
          merged.push({
            id: idValue(record) || `employee-${qualIdValue(record)}`,
            qualificationId: record.qualificationId,
            name: record.name || '',
            referenceNumber: record.referenceNumber || '',
            issueDate: dateValue(record.issueDate),
            expiryDate: dateValue(record.expiryDate),
            qualificationUrl: record.qualificationUrl || record.fileContent || '',
            documentId: record.documentId || record.documents?.[0]?.id || null,
            employeeKeyPayId: record.employeeKeyPayId || currentEmployeeKeyPayId,
            status: getQualificationStatus(record.status, record.expiryDate, record.issueDate, record.referenceNumber),
            _pending: String(record.status || '').toLowerCase() === 'pending'
          });
        });
      const unavailableOptions = new Set(['pending', 'accepted', 'expired']);
      const unavailableQualificationIds = new Set(
        [...employeeRecords, ...pendingRecords]
          .filter((record) => {
            if (String(record.status || '').toLowerCase() === 'not submitted') return false;
            const status = getQualificationStatus(
              record.status,
              record.expiryDate,
              record.issueDate,
              record.referenceNumber
            ).toLowerCase();
            return unavailableOptions.has(status);
          })
          .map((record) => qualIdValue(record))
      );
      const availableOptions = qbo
        .filter((item) => !unavailableQualificationIds.has(qualIdValue(item)))
        .map((item) => ({ value: item, label: item.name }));
      const optionIds = new Set(availableOptions.map(({ value }) => qualIdValue(value)));
      const optionNames = new Set(
        availableOptions.map(({ label }) =>
          String(label || '')
            .trim()
            .toLowerCase()
        )
      );
      setQualifications(availableOptions);
      setRows(
        merged.filter(
          (row) =>
            !optionIds.has(String(row.qualificationId || '')) &&
            !optionNames.has(
              String(row.name || '')
                .trim()
                .toLowerCase()
            )
        )
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not load your qualifications');
    } finally {
      hasLoaded.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const columns = useMemo(
    () => [
      { name: 'name', title: 'Name' },
      { name: 'expiryDate', title: 'Expiry Date' },
      { name: 'issueDate', title: 'Issue Date' },
      { name: 'referenceNumber', title: 'Reference Number' },
      { name: 'qualificationUrl', title: 'Qualification File' },
      { name: 'status', title: 'Status' }
    ],
    []
  );
  const editingStateColumnExtensions = useMemo(
    () =>
      columns.map(({ name }) => ({
        columnName: name,
        editingEnabled: EDITABLE_COLUMNS.includes(name) || name === 'name'
      })),
    [columns]
  );
  const rowId = (row) => row.id;
  const [imageColumn] = useState(['qualificationUrl']);
  const openQualificationDetails = useCallback(
    (targetRowId) => {
      if (addedRows.length) return;
      editingModeRef.current = 'popup';
      setEditingCellIds([]);
      setEditingRowIds([targetRowId]);
    },
    [addedRows.length]
  );
  const qualificationRowComponent = useCallback(
    (props) => <ClickableMyQualificationRow {...props} onOpen={openQualificationDetails} isAdmin={isAdmin} />,
    [isAdmin, openQualificationDetails]
  );
  const qualificationCellComponent = useCallback(
    (props) => {
      const isDraft = props.row?.id === 'my-qualification-draft';
      const canStartInlineEdit =
        !isDraft && INLINE_COLUMNS.includes(props.column?.name) && (isAdmin || !isPendingRow(props.row));
      return <QualificationFocusableCell {...props} onClick={canStartInlineEdit ? props.onClick : undefined} />;
    },
    [isAdmin]
  );

  const submitQualification = useCallback(
    async (row) => {
      const selectedQual = row.name?.value;
      const name = selectedQual?.name || (typeof row.name === 'string' ? row.name : row.name?.label) || '';
      const rawQualId = row.qualificationId || selectedQual?.id || selectedQual?.qualificationId;
      const qualificationId = Number(rawQualId) || rawQualId;

      if (!qualificationId || !name || !String(row.referenceNumber || '').trim()) {
        toast.error('Name and reference number are required');
        return null;
      }
      if (!NO_DATE_QUALIFICATIONS.includes(name) && !row.expiryDate && !row.issueDate) {
        toast.error('Enter an issue date or expiry date');
        return null;
      }
      const documentId = row.documentId && !Number.isNaN(Number(row.documentId)) ? Number(row.documentId) : null;
      const qualificationData = {
        qualificationId,
        name,
        referenceNumber: String(row.referenceNumber).trim(),
        expiryDate: row.expiryDate ? `${row.expiryDate}T00:00:00` : null,
        issueDate: row.issueDate ? `${row.issueDate}T00:00:00` : null,
        documentId
      };
      // The employee read endpoint can return a blob: preview made from QBO
      // document bytes. Never persist that temporary browser URL as the file URL.
      if (
        typeof row.qualificationUrl === 'string' &&
        row.qualificationUrl.trim() &&
        !row.qualificationUrl.startsWith('blob:')
      ) {
        qualificationData.qualificationUrl = row.qualificationUrl;
      }
      const formData = new FormData();
      formData.append('qualificationData', JSON.stringify(qualificationData));
      formData.append('userId', localStorage.getItem('userId'));
      if (qualificationData.documentId) formData.append('documentId', String(qualificationData.documentId));
      if (isFile(row.qualificationUrl) && !row._attachmentFromQbo) formData.append('avatar', row.qualificationUrl);
      setSubmitting(true);
      try {
        let response;
        if (isAdmin) {
          qualificationData.employeeKeyPayId = row.employeeKeyPayId || employeeKeyPayId;
          qualificationData.status = 'accepted';
          if (!qualificationData.employeeKeyPayId) {
            toast.error('Could not identify the employee for this qualification');
            return null;
          }
          formData.set('qualificationData', JSON.stringify(qualificationData));
          formData.delete('documentId');
          if (isFile(row.qualificationUrl) && !row._attachmentFromQbo) {
            formData.delete('avatar');
            formData.append('file', row.qualificationUrl);
          }
          response = await employeeQualificationService.updateEmployeeQualificationInQboByAdmin(formData);
          toast.success(response?.data?.message || 'Qualification updated and accepted');
        } else {
          response = await employeeQualificationService.notifyAdminEmployeeQualificationUpdate(formData);
          toast.success(response?.data?.message || 'Qualification submitted for review');
        }
        await loadRows();
        const saved = response?.data?.data;
        const savedStatus = isAdmin ? 'Accepted' : 'Pending';
        setRows((current) =>
          current.map((item) =>
            String(item.id) === String(row.id)
              ? {
                  ...item,
                  ...row,
                  ...(saved && typeof saved === 'object' ? saved : {}),
                  status: savedStatus,
                  _pending: !isAdmin
                }
              : item
          )
        );
        return {
          ...(saved && typeof saved === 'object' ? saved : {}),
          status: savedStatus,
          _pending: !isAdmin
        };
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          (typeof error?.response?.data?.error === 'string' ? error?.response?.data?.error : null) ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Could not submit qualification';
        toast.error(message);
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [employeeKeyPayId, isAdmin, loadRows]
  );

  const commitChanges = useCallback(
    async ({ added, changed }) => {
      if (added?.length) {
        const row = added[0];
        const saved = await submitQualification(row);
        if (saved) {
          setAddedRows([]);
          cancelConversion();
        }
        return;
      }
      for (const [id, changes] of Object.entries(changed || {})) {
        const row = latestRows.current.find((item) => String(item.id) === id);
        if (!row) continue;
        const updatedRow = {
          ...row,
          ...changes,
          ...(isFile(changes.qualificationUrl) ? { _attachmentFromQbo: false } : {})
        };
        await submitQualification(updatedRow);
      }
    },
    [cancelConversion, submitQualification]
  );

  const selectFileForDraft = useCallback(
    (event, row) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      selectFile(file, (prepared) => {
        setAddedRows((current) =>
          current.map((item) =>
            item.id === row.id ? { ...item, qualificationUrl: prepared, _attachmentFromQbo: false } : item
          )
        );
      });
    },
    [selectFile]
  );
  const changeDraftQualification = useCallback(
    (selected) => {
      const selectedQualId = selected?.value?.qualificationId || selected?.value?.id;
      const matchingRow = rows.find((item) => String(item.qualificationId) === String(selectedQualId));
      setAddedRows((current) =>
        current.map((item) =>
          item.id === 'my-qualification-draft'
            ? {
                ...item,
                name: selected,
                qualificationId: selectedQualId,
                documentId: matchingRow?.documentId || null
              }
            : item
        )
      );
    },
    [rows]
  );
  const editCell = useCallback(
    (props) => (
      <QualificationTableEditCell
        {...props}
        qualificationOptions={qualifications}
        onQualificationChange={
          props.column.name === 'name' && props.row?.id === 'my-qualification-draft'
            ? changeDraftQualification
            : undefined
        }
        onSelectFile={selectFileForDraft}
        fileBusy={isConverting}
        fileError={fileError}
      />
    ),
    [changeDraftQualification, fileError, isConverting, qualifications, selectFileForDraft]
  );
  const handleAddedRowsChange = useCallback(
    (newAddedRows) => {
      setAddedRows(newAddedRows);
      if (newAddedRows.length) {
        setEditingCellIds([]);
        editingModeRef.current = null;
      } else {
        cancelConversion();
      }
    },
    [cancelConversion]
  );
  const addQualification = useCallback(() => {
    if (addedRows.length) return;
    setAddedRows([
      {
        id: 'my-qualification-draft',
        name: null,
        referenceNumber: '',
        issueDate: '',
        expiryDate: '',
        qualificationUrl: null,
        documentId: null
      }
    ]);
  }, [addedRows.length]);

  if (loading)
    return (
      <div style={{ padding: 24 }}>
        <CircularProgress size={24} /> Loading qualifications…
      </div>
    );
  return (
    <Paper>
      <Grid rows={rows} columns={columns} getRowId={rowId}>
        <CalendarProvider for={['expiryDate', 'issueDate']} />
        <DataTypeProvider for={['status']} formatterComponent={StatusFormatter} />
        <DataTypeProvider for={['name']} formatterComponent={QualificationNameFormatter} />
        <ImageProvider for={imageColumn} />
        <EditingState
          editingCellIds={editingCellIds}
          onEditingCellIdsChange={(ids) => {
            if (addedRows.length) {
              setEditingCellIds([]);
              editingModeRef.current = null;
              return;
            }
            if (editingModeRef.current === 'popup') return;
            const editableIds = ids.filter((item) => {
              const row = rows.find((current) => String(current.id) === String(item.rowId));
              return INLINE_COLUMNS.includes(item.columnName) && (isAdmin || !isPendingRow(row));
            });
            editingModeRef.current = editableIds.length ? 'inline' : null;
            setEditingCellIds(editableIds);
          }}
          editingRowIds={editingRowIds}
          onEditingRowIdsChange={(ids) => {
            const editableRowIds = ids.filter(
              (id) => isAdmin || !isPendingRow(rows.find((row) => String(row.id) === String(id)))
            );
            if (editableRowIds.length) {
              editingModeRef.current = 'popup';
              setEditingCellIds([]);
            } else if (editingModeRef.current === 'popup') {
              editingModeRef.current = null;
            }
            setEditingRowIds(editableRowIds);
          }}
          onCommitChanges={commitChanges}
          addedRows={addedRows}
          onAddedRowsChange={handleAddedRowsChange}
          columnExtensions={editingStateColumnExtensions}
        />
        <SortingState sorting={sorting} onSortingChange={setSorting} />
        <IntegratedSorting />
        <SearchState />
        <IntegratedFiltering />
        <PagingState
          currentPage={currentPage}
          onCurrentPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
        <IntegratedPaging />
        <Table rowComponent={qualificationRowComponent} cellComponent={qualificationCellComponent} />
        <TableColumnResizing defaultColumnWidths={WIDTHS} />
        <TableHeaderRow showSortingControls />
        <Toolbar
          rootComponent={(props) => (
            <ToolbarRoot
              {...props}
              onAdd={addQualification}
              adding={Boolean(addedRows.length)}
              submitting={submitting}
            />
          )}
        />
        <SearchPanel />
        <TableEditRow cellComponent={editCell} />
        <TableEditColumn
          showEditCommand
          width={140}
          cellComponent={(props) => <QualificationCommandsCell {...props} isAdmin={isAdmin} />}
        />
        <TableInlineCellEditing startEditAction="click" selectTextOnEditStart />
        <PopupEditing
          popupComponent={MyQualificationPopup}
          setEmployeesQualifications={setRows}
          rows={rows}
          saveRow={submitQualification}
        />
        <PagingPanel pageSizes={[25, 50, 100]} />
      </Grid>
      {addedRows.length > 0 && (
        <div style={{ padding: '8px 16px', color: '#666' }}>
          Choose a qualification, enter its reference and required date, attach an image or PDF, then save the row.
        </div>
      )}
    </Paper>
  );
};

export default MyQualificationTable;
